import React, { useState } from 'react';
import PropTypes from 'prop-types';

import {
  Line,
  YAxis,
  XAxis,
  Area,
  Label,
  ComposedChart,
  CartesianGrid,
  Customized,
  ResponsiveContainer,
} from 'recharts';

import SrBox from 'client/components/SrBox';
import SrDollarSign from 'client/components/SrPriceMarketFitSunSpot/SrDollarSign';
import SrPriceTooltip from 'client/components/SrPriceMarketFitSunSpot/SrPriceTooltip';
import SrDayOnMarketTick from 'client/components/SrPriceMarketFitSunSpot/SrDayOnMarketTick';
import SrTodayDot from 'client/components/SrPriceMarketFitSunSpot/SrTodayDot';
import SrTodayTooltip from 'client/components/SrPriceMarketFitSunSpot/SrTodayTooltip';

const axisStyle = {
  fontFamily: 'Greycliff',
  fill: 'rgba(0, 0, 0, 0.4)',
};

const labelStyle = {
  fontFamily: 'Greycliff',
  fill: '#1C1C1C',
};

function findADaysOnMarketPoints(arr, value) {
  let lowerValue = null;
  let higherValue = null;

  for (let i = 0; i < arr.length; i += 1) {
    if (arr[i].payload.daysOnMarket <= value) {
      lowerValue = arr[i];
    }
    if (arr[i].payload.daysOnMarket >= value) {
      higherValue = arr[i];
      break;
    }
  }

  return [lowerValue, higherValue];
}

function getCXDaysOnMarket(p1, p2, priceChange) {
  const xDiff = p2.x - p1.x;
  const daysDiff = priceChange.daysOnMarket - p1.payload.daysOnMarket;
  const cx = ((daysDiff * xDiff) / 7) + p1.x;

  return cx;
}

function getCY(x1, y1, x2, y2, x) {
  const y = y1 + ((y2 - y1) / (x2 - x1) || 0) * (x - x1);

  return y;
}

const Prices = ({
  priceChanges,
  formattedGraphicalItems,
  graphIndex,
}) => {
  const leadsGraphPoints = formattedGraphicalItems[graphIndex].props.points;

  const dollarSigns = priceChanges.map((priceChange) => {
    const points = findADaysOnMarketPoints(leadsGraphPoints, priceChange.daysOnMarket);

    const cx = getCXDaysOnMarket(points[0], points[1], priceChange);
    const cy = getCY(points[0].x, points[0].y, points[1].x, points[1].y, cx);

    return (
      <SrDollarSign
        key={priceChange.daysOnMarket}
        cy={cy}
        cx={cx}
      />
    );
  });

  return dollarSigns;
};

const PriceToolTips = ({
  priceChanges,
  formattedGraphicalItems,
  graphIndex,
}) => {
  const leadsGraphPoints = formattedGraphicalItems[graphIndex].props.points;

  const dollarSigns = priceChanges.map((priceChange) => {
    const points = findADaysOnMarketPoints(leadsGraphPoints, priceChange.daysOnMarket);

    const cx = getCXDaysOnMarket(points[0], points[1], priceChange);
    const cy = getCY(points[0].x, points[0].y, points[1].x, points[1].y, cx);

    return (
      <SrPriceTooltip
        key={priceChange.daysOnMarket}
        cy={cy}
        cx={cx}
        price={priceChange.price}
      />
    );
  });

  return dollarSigns;
};

const SunSpotLabel = ({
  listing,
  formattedGraphicalItems,
}) => {
  const initialPoint = formattedGraphicalItems[1].props.points[0];
  const y = listing.avgLeadsComposition > 12 ? -3 : 14;

  return (
    <svg
      x={initialPoint.x + 2}
      y={initialPoint.y + y}
      width='60'
      height='38'
      viewBox='0 0 60 38'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <text
        x={60}
        y={5}
        dy={18}
        textAnchor='end'
        fontFamily='Greycliff'
        fontSize='14px'
        fontWeight='700'
        fill='#000000'
        opacity={0.3}
      >
        SunSpot
      </text>
    </svg>
  );
};

const TodayDot = ({
  listing,
  formattedGraphicalItems,
  graphIndex,
}) => {
  const leadsGraphPoints = formattedGraphicalItems[graphIndex].props.points;
  const points = findADaysOnMarketPoints(leadsGraphPoints, listing.daysOnMarket);
  const cx = getCXDaysOnMarket(points[0], points[1], { daysOnMarket: listing.daysOnMarket });
  const cy = getCY(points[0].x, points[0].y, points[1].x, points[1].y, cx);

  return (
    <>
      <SrTodayDot
        cx={cx}
        cy={cy}
      />
      <SrTodayTooltip
        cx={cx}
        cy={cy}
      />
    </>
  );
};

const Calculation = ({
  yAxisMap,
  onLoad,
}) => {
  if (yAxisMap[0]) {
    onLoad(yAxisMap[0].niceTicks[yAxisMap[0].niceTicks.length - 1]);
  }

  return null;
};

const SrPriceMarketFitSunSpotChart = ({
  listing,
  weeklyLeads,
  priceChanges,
}) => {
  const [maxY, setMaxY] = useState(null);

  const showSunSpot = !!(
    listing.avgLeadsComposition
    && listing.avgLeadsCompositionDate
  );

  const data = maxY === null
    ? weeklyLeads
    : weeklyLeads.map((weeklyLead) => {
      return {
        ...weeklyLead,
        hot:  maxY - weeklyLead.cold - weeklyLead.sunSpot,
      };
    });

  const graphIndex = showSunSpot ? (maxY === null
    ? 2
    : 3) : 0;

  return (
    <SrBox bgColor='white' padding='30px 20px 20px 10px' borderRadius='14px'>
      <ResponsiveContainer width='100%' height={450}>
        <ComposedChart
          data={data}
          margin={{ top: 10, bottom: 50, right: 26 }}
        >
          <defs>
            <linearGradient id='hot' x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor='#EE4459' stopOpacity={1} />
              <stop offset='100%' stopColor='#FCDCE0' stopOpacity={1} />
            </linearGradient>
          </defs>
          <defs>
            <linearGradient id='cold' x1='0' y1='0' x2='0' y2='1'>
              <stop offset='5%' stopColor='#D6E9FF' stopOpacity={1} />
              <stop offset='170%' stopColor='#007AFF' stopOpacity={1} />
            </linearGradient>
          </defs>
          <YAxis
            axisLine={false}
            tickLine={false}
            style={axisStyle}
          >
            <Label angle={-90} position='insideCenterLeft' style={labelStyle}>
              Leads per Week
            </Label>
          </YAxis>
          <XAxis
            tickLine={false}
            axisLine={false}
            dataKey='daysOnMarket'
            style={axisStyle}
            padding={{ bottom: 10 }}
            tick={<SrDayOnMarketTick data={weeklyLeads} />}
          >
            <Label offset={30} position='bottom' style={labelStyle}>
              Days on Market
            </Label>
          </XAxis>
          {showSunSpot && (
            <>
              <Area
                type='monotone'
                dataKey='cold'
                stackId='1'
                fillOpacity={1}
                stroke={0}
                fill='url(#cold)'
              />
              <Area
                type='monotone'
                dataKey='sunSpot'
                stackId='1'
                stroke={0}
                fillOpacity={1}
                fill='#FFF5CC'
              />
              <Area
                type='monotone'
                dataKey='hot'
                stackId='1'
                stroke={0}
                fillOpacity={1}
                fill='url(#hot)'
              />
              <Customized
                component={(props) => {
                  return (
                    <SunSpotLabel
                      {...props}
                      listing={listing}
                    />
                  );
                }}
              />
              <Customized
                component={(props) => {
                  return (
                    <Calculation
                      {...props}
                      listing={listing}
                      onLoad={(maxY) => {
                        setMaxY(maxY);
                      }}
                    />
                  );
                }}
              />
            </>
          )}
          <Line
            type='monotone'
            dataKey='leads'
            stroke='#FFCD00'
            strokeWidth={5}
            fill='#FFCD00'
            filter='drop-shadow(0px 5px 5px rgba(0, 0, 0, 0.13))'
            dot={false}
          />
          <CartesianGrid vertical={false} stroke='rgba(0, 0, 0, 0.08)' fill={false} />
          <Customized
            component={(props) => {
              return (
                <Prices
                  {...props}
                  graphIndex={graphIndex}
                  priceChanges={priceChanges}
                />
              );
            }}
          />
          <Customized
            component={(props) => {
              return (
                <PriceToolTips
                  {...props}
                  graphIndex={graphIndex}
                  priceChanges={priceChanges}
                />
              );
            }}
          />
          <Customized
            component={(props) => {
              return (
                <TodayDot
                  {...props}
                  listing={listing}
                  graphIndex={graphIndex}
                />
              );
            }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </SrBox>
  );
};

SrPriceMarketFitSunSpotChart.propTypes = {
  weeklyLeads: PropTypes.array.isRequired,
  priceChanges: PropTypes.array.isRequired,
};

SrPriceMarketFitSunSpotChart.displayName = 'SrPriceMarketFitSunSpotChart';

export default SrPriceMarketFitSunSpotChart;
